<script setup lang="ts">
/**
 * Icône « ? » avec panneau d’aide au survol (SourceToolHoverCard).
 * Peut afficher un libellé à gauche (titres de carte) ou seulement l’icône (en-têtes de tableau).
 */
import SourceToolHoverCard from '~/components/ui/SourceToolHoverCard.vue'

defineOptions({ name: 'SourceFieldHelp' })

const props = withDefaults(
  defineProps<{
    /** Texte affiché à gauche de l’icône (optionnel). */
    label?: string
    /** Titre du panneau hover (défaut = label). */
    title?: string
    description: string
    openDelayMs?: number
  }>(),
  {
    label: undefined,
    title: undefined,
    openDelayMs: 180,
  }
)

const hoverTitle = computed(() => props.title || props.label || 'Aide')
</script>

<template>
  <span class="source-field-help" :class="{ 'source-field-help--with-label': Boolean(label) }">
    <span v-if="label" class="source-field-help__label">{{ label }}</span>
    <SourceToolHoverCard
      :title="hoverTitle"
      :description="description"
      :open-delay-ms="openDelayMs"
      :side-offset-px="8"
    >
      <template #trigger>
        <button
          type="button"
          class="source-field-help__trigger"
          :aria-label="`Aide : ${hoverTitle}`"
        >
          <span class="material-symbols-rounded" aria-hidden="true">help</span>
        </button>
      </template>
    </SourceToolHoverCard>
  </span>
</template>

<style scoped lang="scss">
@use "~/assets/scss/partials/elpdl/colors" as c;
@use "~/assets/scss/partials/elpdl/spacing" as *;

.source-field-help {
  display: inline-flex;
  align-items: center;
  gap: $spacing-xs;
  min-width: 0;
}

.source-field-help--with-label {
  width: 100%;
}

.source-field-help__label {
  display: block;
  margin: 0;
  flex: 1 1 auto;
  min-width: 0;
  font-family: Public Sans, sans-serif;
  font-size: 1rem;
  font-weight: 700;
  line-height: 1.25rem;
  color: c.$enedis-neutral-900;
}

.source-field-help__trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  margin: 0;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: transparent;
  color: c.$enedis-neutral-500;
  cursor: help;

  .material-symbols-rounded {
    font-size: 1.125rem;
    font-variation-settings: "FILL" 0, "wght" 500, "GRAD" 0, "opsz" 20;
    line-height: 1;
  }

  &:hover,
  &:focus-visible {
    color: c.$enedis-primary-700;
    background: c.$enedis-primary-200;
  }

  &:focus-visible {
    outline: 2px solid c.$enedis-primary-500;
    outline-offset: 1px;
  }
}
</style>
